// ============================================================================
// ContactSelect — a pickup/drop contact chooser for the Delhivery booking flow. Built from the
// quote's dynamic {label, name, value} options plus a fixed Delivery Team roster; the number
// `value` of the chosen option is what gets sent. Picking "Other…" reveals a free-text box.
// ============================================================================
import { Input, Select, Space } from "antd";
import React from "react";

export interface ContactOption {
  label: string;
  name: string | null;
  value: string;
}

// Per-type display overrides, keyed by the backend option's label (or name). `display` replaces the
// label text; `name` controls the parenthetical — null hides it, a string fills in when the option
// has no name of its own, and omitting the key keeps the option's own name.
const OVERRIDES: Record<string, { display: string; name?: string | null }> = {
  "Booking POC": { display: "Me", name: null },
  "Vendor contact": { display: "Vendor Contact" },
  "Delivery team": { display: "Delivery Team" },
};

// The full Delivery Team roster, always offered in every contact dropdown on top of whatever the
// quote returns. It supersedes the backend's own MD delivery-team option, which is dropped (see
// isMdDeliveryTeam); backend duplicates of these numbers also defer to the roster entry.
const DELIVERY_TEAM_CONTACTS: ContactOption[] = [
  { label: "Delivery team", name: "Keshav", value: "9187695027" },
  { label: "Delivery team", name: "Kartik", value: "9187953209" },
  { label: "Delivery team", name: "Nadeaem", value: "9663426218" },
  { label: "Delivery team", name: "Sheik", value: "7204951001" },
  { label: "Delivery team", name: "Venkatesh", value: "9187920944" },
  { label: "Delivery team", name: "Raghu", value: "9187230659" },
  { label: "Delivery team", name: "Kiran", value: "9606018735" },
  { label: "Delivery team", name: "Prem", value: "9663426164" },
  { label: "Delivery team", name: "Vijay", value: "9686727641" },
];

// Phone number → Delivery Team member name, for displaying a known contact by name. Returns null
// when the number isn't in the roster (caller then shows just the number).
export const deliveryTeamName = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const digits = value.replace(/\D/g, "");
  const match = DELIVERY_TEAM_CONTACTS.find(
    (c) => c.value.replace(/\D/g, "") === digits
  );
  return match?.name ?? null;
};

// Backend delivery-team options (either label variant) are replaced by the roster above.
const isMdDeliveryTeam = (o: ContactOption) =>
  o.label === "MD delivery team" || o.label === "Delivery team";

// "{display} (name) - {value}" — e.g. "Me - 9833…", "Vendor Contact (Ramesh) - 98…",
// "Delivery Team (Keshav) - 91…".
export const contactOptionLabel = (o: ContactOption) => {
  const ov = OVERRIDES[o.label] ?? OVERRIDES[o.name ?? ""];
  const displayLabel = ov?.display ?? o.label;
  const nm = ov && "name" in ov ? (ov.name === null ? null : o.name ?? ov.name) : o.name;
  return nm ? `${displayLabel} (${nm}) - ${o.value}` : `${displayLabel} - ${o.value}`;
};

// The default selection: the option whose value matches the quote's contact number, else the first
// option, else the current value (which may be empty). Keeps a matching number selected across quotes.
export const defaultContactValue = (
  options: ContactOption[] | undefined,
  current: string | null | undefined
): string => {
  const opts = (options ?? []).filter((o) => !isMdDeliveryTeam(o));
  if (opts.length === 0) return current ?? "";
  const match = opts.find((o) => o.value === current);
  return match ? match.value : opts[0].value;
};

const OTHER = "__other__";

const ContactSelect: React.FC<{
  options?: ContactOption[];
  value: string;
  onChange: (v: string) => void;
  width?: number;
  // Special non-phone options prepended to the dropdown (e.g. an "apply each booking's own vendor
  // contact" action). Their values are treated as known, so they never trigger "Other" mode.
  extraOptions?: { label: string; value: string }[];
}> = ({ options, value, onChange, width = 260, extraOptions }) => {
  const extras = extraOptions ?? [];
  const backendOpts = (options ?? []).filter((o) => !isMdDeliveryTeam(o));
  const opts = [
    ...backendOpts,
    ...DELIVERY_TEAM_CONTACTS.filter(
      (d) => !backendOpts.some((o) => o.value === d.value)
    ),
  ];
  const isKnown = (v: string) =>
    opts.some((o) => o.value === v) || extras.some((e) => e.value === v);

  // "Other" mode: a value the option list doesn't contain (a hand-typed number). Re-derive whenever
  // the option set changes (a fresh quote), so the box doesn't get stuck open across re-quotes.
  const [other, setOther] = React.useState(!!value && !isKnown(value));
  React.useEffect(() => {
    setOther(!!value && !isKnown(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  return (
    <Space.Compact>
      {/* Let each dropdown option wrap onto a second line instead of truncating with an ellipsis. */}
      <style>{`
        .delhivery-contact-dropdown .ant-select-item-option-content {
          white-space: normal;
          word-break: break-word;
          line-height: 1.3;
        }
        .delhivery-contact-dropdown .ant-select-item {
          height: auto;
        }
      `}</style>
      <Select
        style={{ width }}
        popupClassName="delhivery-contact-dropdown"
        value={other ? OTHER : value || undefined}
        placeholder="Select a contact"
        optionLabelProp="label"
        onChange={(v) => {
          if (v === OTHER) {
            setOther(true);
            onChange("");
          } else {
            setOther(false);
            onChange(v);
          }
        }}
        options={[
          ...extras,
          ...opts.map((o) => ({ label: contactOptionLabel(o), value: o.value })),
          { label: "Other…", value: OTHER },
        ]}
      />
      {other && (
        <Input
          style={{ width: 150 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="10-digit number"
        />
      )}
    </Space.Compact>
  );
};

export default ContactSelect;
